/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo, useEffect, useState } from 'react';

import Icon from '@/components/Icon';
import { usePublicWechatGroups } from '@/services';

import './index.scss';

const desktopMediaQuery = '(min-width: 992px)';

const isDesktopViewport = () => {
  return (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function' ||
    window.matchMedia(desktopMediaQuery).matches
  );
};

const WechatGroupFloat: FC = () => {
  const [open, setOpen] = useState(isDesktopViewport);
  const [activeIndex, setActiveIndex] = useState(0);
  const { data } = usePublicWechatGroups();
  const groups = (data || []).slice(0, 5);
  const activeGroup = groups[activeIndex] || groups[0];

  useEffect(() => {
    if (activeIndex >= groups.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, groups.length]);

  if (!activeGroup) {
    return null;
  }

  return (
    <div
      className={`wechat-group-float ${open ? 'is-open' : ''}`}
      onMouseEnter={() => {
        if (isDesktopViewport()) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (isDesktopViewport()) {
          setOpen(false);
        }
      }}>
      <button
        type="button"
        className="wechat-group-float-trigger"
        aria-expanded={open}
        aria-label={open ? '收起交流群二维码' : '打开交流群二维码'}
        onClick={() => setOpen((value) => !value)}>
        <Icon name="wechat" className="wechat-group-float-icon" />
        <span>交流群</span>
      </button>

      <div className="wechat-group-float-panel" aria-hidden={!open}>
        <div className="wechat-group-float-title">
          {activeGroup.title || '微信交流群'}
        </div>
        <img
          src={activeGroup.qr_image_url}
          alt={`${activeGroup.title || '微信交流群'}二维码`}
        />
        <div className="wechat-group-float-note">
          {activeGroup.note ||
            activeGroup.display_name ||
            activeGroup.username ||
            '扫码加入交流群'}
        </div>
        <a
          className="wechat-group-float-save"
          href={activeGroup.qr_image_url}
          download
          target="_blank"
          rel="noreferrer">
          <Icon name="download" />
          <span>点击保存</span>
        </a>
        {groups.length > 1 ? (
          <div className="wechat-group-float-tabs">
            {groups.map((group, index) => (
              <button
                key={group.id}
                type="button"
                className={index === activeIndex ? 'is-active' : ''}
                aria-label={`显示第 ${index + 1} 个微信群二维码`}
                onClick={() => setActiveIndex(index)}>
                {index + 1}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default memo(WechatGroupFloat);
